import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, EmptyState } from "@/components/common";
import { DownloadCloud, Upload, CheckCircle2, AlertTriangle, FileJson } from "lucide-react";

const SAMPLE = {
  source: "polaris-estimation",
  estimate_id: "EST-2026-0142",
  project: {
    name: "Divine Residential — FR Doors",
    client_name: "Aroma International Contracting",
    contract_value_aed: 1042500.0,
    boq_line_count: 18,
  },
  stage_hours: { production: 862.5, installation: 410.0 },
  material_list: [
    { name: "18mm MR MDF 4x8", quantity: 42, unit: "sht", bucket: "Material" },
    { name: "FD60 Door Core 1220x2440x54mm", quantity: 18, unit: "sht", bucket: "Material" },
    { name: "Butt Hinges", quantity: 54, unit: "nos", bucket: "Hardware" },
    { name: "Veneer — Oak", quantity: 96, unit: "m2", bucket: "Finishing" },
  ],
  boq_lines: [],
};

export default function ImportEstimate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState(JSON.stringify(SAMPLE, null, 2));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  if (user?.role !== "commercial" && user?.role !== "ceo") {
    return (
      <Card className="p-2">
        <EmptyState icon={AlertTriangle} title="Senior roles only" subtitle="Only Commercial or Admin can import awarded projects from the estimator." />
      </Card>
    );
  }

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setText(await f.text());
  };

  const submit = async () => {
    setBusy(true); setErr(""); setResult(null);
    let payload;
    try {
      payload = JSON.parse(text);
    } catch (e) {
      setErr("Invalid JSON: " + e.message); setBusy(false); return;
    }
    try {
      const { data } = await api.post("/import/estimate", payload);
      setResult(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-3xl tracking-tight" data-testid="page-title">Import from Estimation</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Paste or upload the handover JSON from Polaris Estimation. The project lands as <b className="text-gold">Awaiting commercial</b> — no scheduling runs until you set its completion date.
        </p>
      </div>

      <Card className="p-5">
        <SectionTitle icon={FileJson}>Handover payload</SectionTitle>
        <div className="flex items-center gap-2 mb-3">
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-border text-sm cursor-pointer hover:bg-muted">
            <Upload size={15} /> Upload .json
            <input type="file" accept="application/json,.json" onChange={onFile} className="hidden" data-testid="import-file" />
          </label>
          <button onClick={() => setText(JSON.stringify(SAMPLE, null, 2))} className="px-3 py-2 rounded-md border border-border text-sm text-muted-foreground hover:bg-muted">
            Load sample
          </button>
        </div>
        <textarea
          data-testid="import-json"
          value={text} onChange={(e) => setText(e.target.value)}
          spellCheck={false} rows={16}
          className="w-full bg-input border border-border rounded-md px-3 py-3 text-xs font-mono outline-none focus:border-gold/50 resize-y"
        />

        {err && <div className="mt-3 text-sm text-critical bg-critical/10 border border-critical/30 rounded-md px-3 py-2" data-testid="import-error">{err}</div>}

        {result && (
          <div className="mt-3 rounded-md border border-success/30 bg-success/10 px-4 py-3" data-testid="import-success">
            <div className="flex items-center gap-2 text-success font-medium">
              <CheckCircle2 size={16} /> Project {result.action} — {result.project.name}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Status: <b>{result.project.status}</b> · {result.notifications} notifications sent · {result.project.material_list?.length || 0} material items on procurement.
            </div>
            <button onClick={() => navigate("/projects")} data-testid="go-to-projects" className="mt-3 px-3 py-1.5 rounded-md bg-gold text-primary-foreground text-sm font-semibold hover:opacity-90">
              Open in Projects → activate
            </button>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={submit} disabled={busy} data-testid="import-submit"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-60">
            <DownloadCloud size={16} /> {busy ? "Importing…" : "Import project"}
          </button>
        </div>
      </Card>
    </div>
  );
}
